import React, { useState, useEffect, useRef } from 'react';
import { InputDropdown } from "./InputDropdown";
import { InputText } from "./InputText";
import { page1, page2_student_q, page2_parent_q } from "../questions";
import { FormButton } from "./FormButton";
import Checkbox from './Checkbox';
import ReCAPTCHA from "react-google-recaptcha";
import '../App.css';
import { logFormError } from '../utils/errorLogger';
import { validateEmail, validatePhone } from '../utils/validation';

export default function Page2({
    userType,
    currentlyIn, setCurrentlyIn,
    graduationYear, setGraduationYear,
    firstName, setFirstName,
    lastName, setLastName,
    email, setEmail,
    phone, setPhone,
    helpText, setHelpText,
    highschool, setHighschool,
    errorMessage,
    setErrorMessage,
    pageTitle,
    pageUrl,
    hubCookie,
    setFormState,
    formState,
    firstNameSub,
    setFirstNameSub,
    lastNameSub,
    setLastNameSub,
    emailSub,
    setEmailSub,
    phoneSub,
    setPhoneSub,
    goToPrevious,
    irClickId,
    customCampaignName,
    partnerid,
    applyingToGradProgram,
    setApplyingToGradProgram,
    }) {
    const [recaptchaComplete, setRecaptchaComplete] = useState(false);
    const recaptchaRef = useRef(null);
    const [smsSub, setSmsSub] = useState(false);

    const submitForm = async (e, check_student_non_college, check_student, check_parent) => {
        try {
            e.preventDefault();

            // Create form data object once for all validations
            const formData = {
                userType,
                currentlyIn,
                firstName,
                lastName,
                email,
                phone,
                helpText,
                graduationYear,
                highschool,
                firstNameSub,
                lastNameSub,
                emailSub,
                phoneSub,
                recaptchaComplete,
                smsSub
            };

            if (!recaptchaComplete) {
                await logFormError(
                    new Error("reCAPTCHA verification not completed"),
                    formData,
                    "recaptcha_incomplete",
                    formState
                );
                setErrorMessage("Please complete the reCAPTCHA verification");
                return;
            }

            if (firstName.length === 0 || lastName.length === 0) {
                await logFormError(
                    new Error("Missing required fields"),
                    formData,
                    "required_fields_missing",
                    formState
                );
                setErrorMessage("Please enter your first and last name");
                return;
            }

            if (!validateEmail(email) && !validatePhone(phone)) {
                await logFormError(
                    new Error("Invalid email and phone validation"),
                    formData,
                    "both_email_and_phone_invalid",
                    formState
                );
                setErrorMessage("Invalid email address and Invalid phone number");
                return;
            }

            if (!validateEmail(email)) {
                setErrorMessage("Invalid email address");
                await logFormError(
                    new Error("Invalid email validation"),
                    formData,
                    "invalid_email",
                    formState
                );
                return;
            }

            if (!validatePhone(phone)) {
                await logFormError(
                    new Error("Invalid phone validation"),
                    formData,
                    "invalid_phone",
                    formState
                );
                setErrorMessage("Invalid phone number");
                return;
            }

            if(helpText.length === 0) {
                await logFormError(
                    new Error("Missing help text"),
                    formData,
                    "help_text_not_provided",
                    formState
                );
                setErrorMessage("Please fill in all required fields");
                return;
            }

            if(check_student) {
                if(highschool.length === 0) {
                    await logFormError(
                        new Error("Missing high school"),
                        formData,
                        "high_school_name_not_provided",
                        formState
                    );
                    setErrorMessage("Please enter your high school name");
                    return;
                }
                if(graduationYear.length === 0) {
                    await logFormError(
                        new Error("Missing graduation year"),
                        formData,
                        "graduation_year_not_provided",
                        formState
                    );
                    setErrorMessage("Please select a graduation year");
                    return;
                }
            }

            if(check_student_non_college) {
                if (highschool.length === 0 || firstNameSub.length === 0 || lastNameSub.length === 0) {
                    await logFormError(
                        new Error("Missing required secondary contact fields"),
                        formData,
                        "secondary_contact_fields_missing",
                        formState
                    );
                    setErrorMessage("Please fill in all required fields");
                    return;
                }
                if (!validateEmail(emailSub) && !validatePhone(phoneSub)) {
                    await logFormError(
                        new Error("Invalid secondary email and phone validation"),
                        formData,
                        "both_secondary_email_and_phone_invalid",
                        formState
                    );
                    setErrorMessage("Invalid email address and Invalid phone number");
                    return;
                } else if (!validateEmail(emailSub)) {
                    await logFormError(
                        new Error("Invalid secondary email validation"),
                        formData,
                        "invalid_secondary_email",
                        formState
                    );
                    setErrorMessage("Invalid email address");
                    return;
                } else if (!validatePhone(phoneSub)) {
                    await logFormError(
                        new Error("Invalid secondary phone validation"),
                        formData,
                        "invalid_secondary_phone",
                        formState
                    );
                    setErrorMessage("Invalid phone number");
                    return;
                }
            }

            if(check_parent){
                if(graduationYear.length === 0) {
                    await logFormError(
                        new Error("Missing graduation year"),
                        formData,
                        "graduation_year_not_selected",
                        "Parent/Guardian",
                        formState
                    );
                    setErrorMessage("Please select a graduation year");
                    return;
                }
            }

            const response = await fetch(`https://prepory-form-backend-ea8tc.kinsta.app/submitForm`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    phone,
                    firstname: firstName,
                    lastname: lastName,
                    pagetitle: pageTitle,
                    pageurl: decodeURIComponent(pageUrl),
                    hubcookie: hubCookie,
                    usertype: userType,
                    helptext: helpText,
                    grad_year: graduationYear,
                    sms_subscription: smsSub,
                    high_school: highschool,
                    secondary_first_name: firstNameSub,
                    secondary_last_name: lastNameSub, 
                    secondary_email: emailSub,
                    secondary_phone: phoneSub,
                    currently_in: currentlyIn,
                    irclickid: irClickId,
                    custom_campaign_name: customCampaignName,
                    partnerid: partnerid,
                    applying_to_a_graduate_program_within_the_next_12_months_: applyingToGradProgram,
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`Submission failed: ${data.message || response.statusText}`);
            }

            if (data.status === 'success') {
                setFormState(formState + 1);

                // Create and dispatch the message
                try {
                    var messageData = JSON.stringify({
                        email,
                        phone,
                        firstname: firstName,
                        lastname: lastName,
                        pagetitle: pageTitle,
                        pageurl: pageUrl,
                        hubcookie: hubCookie,
                        usertype: userType,
                        helptext: helpText,
                        grad_year: graduationYear,
                        sms_subscription: smsSub,
                        high_school: highschool,
                        secondary_first_name: firstNameSub,
                        secondary_last_name: lastNameSub, 
                        secondary_email: emailSub,
                        secondary_phone: phoneSub,
                        currently_in: currentlyIn,
                        irclickid: irClickId,
                        eventName: "submitForm"
                    })
                    try {
                        window.parent.postMessage(messageData, 'https://prepory.com');
                    } catch (error) {
                        logFormError(error, {
                            component: 'PostMessage',
                            errorType: 'post_message_error',
                            messageData,
                            formState
                        });
                    }
                } catch (error) {
                    // Pass if this fails 
                    console.log(error)
                }

                return data;
            } else {
                throw new Error(data.message || 'Unknown submission error');
            }
        } catch (error) {
            await logFormError(error, {
                formData: {
                    email,
                    firstName,
                    lastName,
                    userType,
                    currentlyIn,
                    graduationYear,
                    highschool,
                    helpText,
                    firstNameSub,
                    lastNameSub,
                    emailSub,
                    phoneSub,
                    smsSub,
                    recaptchaComplete
                },
                lastAction: 'form_submission',
                formState,
                validationState: {
                    emailValid: validateEmail(email),
                    phoneValid: validatePhone(phone),
                    recaptchaComplete
                },
                networkState: {
                    online: navigator.onLine,
                    connectionType: navigator.connection?.type,
                    effectiveType: navigator.connection?.effectiveType
                }
            });
            
            // Check if the error is specifically about invalid email
            if (error.message === 'invalid email' || 
                (error.response?.data?.message === 'invalid email') ||
                (error.response?.status === 400 && error.response?.data?.message === 'invalid email')) {
                setErrorMessage("Invalid email. Please try a different one.");
            } else {
                setErrorMessage("An error occurred while submitting the form. Please refresh the page and try again.");
            }
        }
    }

    const handleCheckChange = () => {
        setSmsSub(!smsSub);
    };

    useEffect(() => {
        setErrorMessage(""); //clear the error message after the user inputs a value
      }, [firstName, lastName, email, phone, helpText, highschool, graduationYear]);

    if(userType === 'Student'){
    return (
        <div>
            <h3 className='my-2 text-3xl font-bold'>{page2_student_q.headline}</h3>
            <div className='boxes'>
                <InputText question={page2_student_q.firstName} default_value={firstName} setDefault={setFirstName}/>
                <InputText question={page2_student_q.lastName} default_value={lastName} setDefault={setLastName}/>
            </div>
            <div className='boxes'>
                <InputText question={page2_student_q.email} default_value={email} setDefault={setEmail}/>
                <InputText question={page2_student_q.phone} default_value={phone} setDefault={setPhone}/>
            </div>
            <div className='boxes'>
                {
                    (currentlyIn.includes('high school'))
                    ? <InputText question={page2_student_q.highSchool} default_value={highschool} setDefault={setHighschool} override_label={'High school name'} />
                    : ((currentlyIn.includes('college'))
                        ? <InputText question={page2_student_q.highSchool} default_value={highschool} setDefault={setHighschool} override_label={'College name'} />
                        : ((currentlyIn.includes('middle'))
                            ? <InputText question={page2_student_q.highSchool} default_value={highschool} setDefault={setHighschool} override_label={'Middle school name'} />
                            : ((currentlyIn.includes('gap'))
                                ? <InputText question={page2_student_q.highSchool} default_value={highschool} setDefault={setHighschool} override_label={'High school name'} />
                                : <InputText question={page2_student_q.highSchool} default_value={highschool} setDefault={setHighschool} />
                            )
                        )
                    )
                }
{
    (currentlyIn.includes('high school'))
    ? <InputDropdown 
        question={{
            ...page2_student_q.graduationYr,
            label: null,
            info: null
        }} 
        setState={setGraduationYear} 
        required={true} 
        default_value={graduationYear} 
        override_label={'High school graduation year'} 
      />
    : ((currentlyIn.includes('college'))
        ? <InputDropdown 
            question={{
                ...page2_student_q.graduationYr,
                label: null,
                info: null
            }} 
            setState={setGraduationYear} 
            required={true} 
            default_value={graduationYear} 
            override_label={'College graduation year'} 
          />
        : ((currentlyIn.includes('middle'))
            ? <InputDropdown 
                question={{
                    ...page2_student_q.graduationYr,
                    label: null,
                    info: null
                }} 
                setState={setGraduationYear} 
                required={true} 
                default_value={graduationYear} 
                override_label={'Middle school graduation year'} 
              />
            : ((currentlyIn.includes('gap'))
            ? <InputDropdown 
                question={{
                    ...page2_student_q.graduationYr,
                    label: null,
                    info: null
                }} 
                setState={setGraduationYear} 
                required={true} 
                default_value={graduationYear} 
                override_label={'High school graduation year'} 
              />
            : <InputDropdown 
                question={{
                    ...page2_student_q.graduationYr,
                    label: null,
                    info: null
                }} 
                setState={setGraduationYear} 
                required={true} 
                default_value={graduationYear} 
                override_label={'Graduation year'}
              />
            )
        )
    )
}
            </div>
            <InputText question={page2_student_q.helpInfo} default_value={helpText} setDefault={setHelpText}/>
            {
                !currentlyIn.includes('In college')
                ?
                <div>
                    <h3 className='mt-8 mb-2 text-3xl font-bold'>{page2_student_q.headline_sub}</h3>
                    <div className='boxes'>
                        <InputText question={page2_student_q.firstName_sub} default_value={firstNameSub} setDefault={setFirstNameSub} />
                        <InputText question={page2_student_q.lastName_sub} default_value={lastNameSub} setDefault={setLastNameSub}/>
                    </div>
                    <div className='boxes'>
                        <InputText question={page2_student_q.email_sub} default_value={emailSub} setDefault={setEmailSub}/>
                        <InputText question={page2_student_q.phone_sub} default_value={phoneSub} setDefault={setPhoneSub}/>
                    </div>
                </div>
                : null
            }

            <div className='my-6'>
            <ReCAPTCHA
            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
            ref={recaptchaRef}
            onChange={()=> {
                console.log('recaptcha complete')
                setRecaptchaComplete(true)
                setErrorMessage('')
            }}
            onError={(err) => {
                logFormError(err, {
                    component: 'ReCAPTCHA',
                    errorType: 'recaptcha_error',
                    formState,
                    validationState: {
                        recaptchaComplete
                    }
                });
                setErrorMessage("ReCAPTCHA verification failed. Please try again.");
                setRecaptchaComplete(false);
            }}
            onExpired={() => {
                logFormError(
                    new Error("ReCAPTCHA expired"),
                    {
                        component: 'ReCAPTCHA',
                        formState
                    },
                    { validationType: "recaptcha_expired" }
                );
                setErrorMessage("ReCAPTCHA expired. Please verify again.");
                setRecaptchaComplete(false);
            }}
            />
            </div>

            <Checkbox
                label="By checking this box and submitting the form you are consenting to be contacted by Prepory Coaching Group LLC by SMS text message. Message & data rates may apply. Reply STOP to opt out of further messaging."
                value={smsSub}
                onChange={handleCheckChange}
            />

            <LegalTerms/>

            <div className="flex flex-row !mt-5">
            {
                currentlyIn.includes('In college')
                ? <FormButton cta="Next" clickAction={(e) => submitForm(e, false, true)} type="submit" style="primary" />
                : <FormButton cta="Next" clickAction={(e) => submitForm(e, true, true)} type="submit" style="primary" />
            }
                <FormButton cta="Back" clickAction={goToPrevious} type="secondary" />
            </div>

        </div>
    )} else {
        // page userType != student
return (
    <div>
        <h3 className='my-2 text-3xl font-bold'>{page2_parent_q.headline}</h3>
        <div className='boxes'>
            <InputText question={page2_parent_q.firstName} default_value={firstName} setDefault={setFirstName} />
            <InputText question={page2_parent_q.lastName} default_value={lastName} setDefault={setLastName} />
        </div>
        <div className='boxes'>
            <InputText question={page2_parent_q.email} default_value={email} setDefault={setEmail} />
            <InputText question={page2_parent_q.phone} default_value={phone} setDefault={setPhone} />
        </div>
        {userType !== 'Graduate School Applicant' && (
    <InputText question={page2_parent_q.helpInfo} default_value={helpText} setDefault={setHelpText} />
)}
        {
    userType === 'Graduate School Applicant' ? (
        <>
            <div className='boxes'>
                <div className="flex flex-col justify-end w-[49%]">
                    <InputDropdown
                        question={{
                            info: "Are you applying to a graduate program within the next 12 months?",
                            options: [
                                { value: "Yes", label: "Yes" },
                                { value: "No", label: "No" },
                                { value: "i_dont_know", label: "I don't know" }
                            ],
                            required: true,
                            id: "applying_to_a_graduate_program_within_the_next_12_months_"
                        }}
                        setState={setApplyingToGradProgram}
                        required={true}
                        default_value={applyingToGradProgram}
                    />
                </div>
                <div className="flex flex-col justify-end w-[49%]">
                    <InputDropdown
                        question={{
                            ...page2_student_q.graduationYr,
                            label: null,
                            info: null,
                            options: page2_student_q.graduationYr.options_grad
                        }}
                        setState={setGraduationYear}
                        required={true}
                        default_value={graduationYear}
                        override_label="College graduation year"
                    />
                </div>
            </div>
            <InputText
                question={{
                    ...page2_student_q.helpInfo,
                    label: null,
                    info: null
                }}
                default_value={helpText}
                setDefault={setHelpText}
                override_label="Can you tell us more about your higher education goals?"
            />
        </>
    ) : userType !== 'School administrator/Counselor' && userType !== 'Employer/Company representative' && userType !== 'Other' ? (
        <>
            <h3 className='!mt-10 mb-2 text-3xl font-bold'>{page2_parent_q.headline_sub}</h3>
            <div className='boxes'>
                <InputText question={page2_parent_q.firstName_sub} default_value={firstNameSub} setDefault={setFirstNameSub} />
                <InputText question={page2_parent_q.lastName_sub} default_value={lastNameSub} setDefault={setLastNameSub} />
            </div>
            <div className='boxes'>
                <InputText question={page2_parent_q.email_sub} default_value={emailSub} setDefault={setEmailSub} />
                <InputText question={page2_parent_q.phone_sub} default_value={phoneSub} setDefault={setPhoneSub} />
            </div>
            <div className='boxes'>
                {
                    userType === 'Parent/Guardian'
                        ? <>
                            <InputText
                                question={{
                                    ...page2_parent_q.highSchool,
                                    label: null,
                                    info: null
                                }}
                                show_secondary_label={false}
                                default_value={highschool}
                                setDefault={setHighschool}
                                override_label="School name"
                            />
                            <InputDropdown
                                question={{
                                    ...page2_parent_q.graduationYr,
                                    label: null,
                                    info: null
                                }}
                                setState={setGraduationYear}
                                required={true}
                                default_value={graduationYear}
                                override_label="Graduation year"
                            />
                        </>
                        : <>
                            <InputText question={page2_parent_q.highSchool} show_secondary_label={true} default_value={highschool} setDefault={setHighschool} />
                            <InputDropdown
                                question={{
                                    ...page1.q2,
                                    options: page1.q2.options,
                                }}
                                setState={setCurrentlyIn}
                                required={false}
                                default_value={currentlyIn}
                            />
                        </>
                }
            </div>
        </>
    ) : null
}

        <div className='my-6'>
        <ReCAPTCHA
        sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
        ref={recaptchaRef}
        onChange={()=> {
            console.log('recaptcha complete')
            setRecaptchaComplete(true)
            setErrorMessage('')
        }}
        onError={(err) => {
            logFormError(err, {
                component: 'ReCAPTCHA',
                errorType: 'recaptcha_error',
                formState,
                validationState: {
                    recaptchaComplete
                }
            });
            setErrorMessage("ReCAPTCHA verification failed. Please try again.");
            setRecaptchaComplete(false);
        }}
        onExpired={() => {
            logFormError(
                new Error("ReCAPTCHA expired"),
                {
                    component: 'ReCAPTCHA',
                    formState
                },
                { validationType: "recaptcha_expired" }
            );
            setErrorMessage("ReCAPTCHA expired. Please verify again.");
            setRecaptchaComplete(false);
        }}
        />
        </div>

        <Checkbox
            label="By checking this box and submitting the form you are consenting to be contacted by Prepory Coaching Group LLC by SMS text message. Message & data rates may apply. Reply STOP to opt out of further messaging."
            value={smsSub}
            onChange={handleCheckChange}
        />
        <LegalTerms/>

        <div className="flex flex-row !mt-5">
            {
                    userType === 'Parent/Guardian'
                    ? <FormButton cta="Next" clickAction={(e) => submitForm(e, false, false, true)} type="submit" style="primary" />
                    : <FormButton cta="Next" clickAction={(e) => submitForm(e, false, false, false)} type="submit" style="primary" />
            }
            <FormButton cta="Back" clickAction={goToPrevious} type="secondary" />
        </div>

    </div>
)
}
}


function LegalTerms() {
return(
    <div className='mt-5'>
        <p className='text-sm'>
        We value your privacy. We use the info you share to keep you updated about our relevant content, products, and services. You can opt-out of these anytime.
        For more details, check out our <a className='text-primary-green' target='_top' href='https://prepory.com/privacy-policy/'>privacy policy</a>.
        </p>
    </div>
)
}
